import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setTodos } from "../../redux/slices/todoSlice";
import {
    useDeleteeTodoMutation,
    useGetsTodosQuery,
} from "../../redux/slices/todoApiSlice";
import { useNavigate } from "react-router-dom";
import {
    Trash2,
    ExternalLink,
    ArrowRight,
    Eye,
    Tag,
    AlertTriangle,
    RefreshCw,
    Loader2,
    Heart,
    PackageOpen,
} from "lucide-react";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Favorie = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // =========================================================
    // FAVORİ MƏHSULLAR
    // =========================================================
    const {
        data,
        isLoading,
        isError,
        error,
        refetch,
        isFetching,
    } = useGetsTodosQuery();

    // =========================================================
    // FAVORİ MƏHSUL SİLMƏ
    // =========================================================
    const [deleteeTodo] = useDeleteeTodoMutation();

    const [deletingId, setDeletingId] = useState(null);

    // =========================================================
    // RTK QUERY-DƏN GƏLƏN MƏLUMATI MÖVCUD REDUX SLICE-Ə YAZIRIQ
    // =========================================================
    useEffect(() => {
        if (data) {
            dispatch(setTodos(data));
        }
    }, [data, dispatch]);

    // =========================================================
    // XƏTA MESAJI
    // =========================================================
    const getErrorMessage = (err) => {
        const serverMessage =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.data?.message ||
            err?.data?.error ||
            err?.message;

        if (serverMessage) {
            return serverMessage;
        }

        if (
            err?.status === 400 ||
            err?.response?.status === 400
        ) {
            return "Göndərilən məlumatlar düzgün deyil.";
        }

        if (
            err?.status === 401 ||
            err?.response?.status === 401
        ) {
            return "Sessiyanız başa çatıb. Zəhmət olmasa, hesabınıza yenidən daxil olun.";
        }

        if (
            err?.status === 403 ||
            err?.response?.status === 403
        ) {
            return "Bu əməliyyatı yerinə yetirmək üçün icazəniz yoxdur.";
        }

        if (
            err?.status === 404 ||
            err?.response?.status === 404
        ) {
            return "Tələb olunan məlumat tapılmadı.";
        }

        if (
            err?.code === "ERR_NETWORK" ||
            err?.message?.toLowerCase()?.includes("network")
        ) {
            return "Serverə qoşulmaq mümkün olmadı. İnternet bağlantınızı yoxlayın.";
        }

        if (
            err?.response?.status >= 500 ||
            err?.status >= 500
        ) {
            return "Serverdə texniki problem baş verdi. Zəhmət olmasa, bir qədər sonra yenidən cəhd edin.";
        }

        return "Naməlum xəta baş verdi. Zəhmət olmasa, yenidən cəhd edin.";
    };

    // =========================================================
    // FAVORİDƏN MƏHSULU SİL
    // =========================================================
    const handleDelete = async (id) => {
        if (!id || deletingId) {
            return;
        }

        setDeletingId(id);

        try {
            await deleteeTodo(id).unwrap();

            /*
             * Burada artıq refetch() yazmırıq.
             *
             * todoApiSlice daxilində:
             *
             * invalidatesTags: ["Favorite"]
             *
             * olduğu üçün RTK Query avtomatik olaraq
             * getsTodos sorğusunu yeniləyəcək.
             */
            toast.success(
                "Məhsul seçilmişlər siyahısından uğurla silindi."
            );
        } catch (err) {
            console.error(
                "Məhsulun seçilmişlərdən silinməsi zamanı xəta:",
                err
            );

            toast.error(
                `Məhsulu seçilmişlər siyahısından silmək mümkün olmadı. Səbəb: ${getErrorMessage(
                    err
                )}`
            );
        } finally {
            setDeletingId(null);
        }
    };

    // =========================================================
    // MƏHSULUN ƏTRAFLI SƏHİFƏSİNƏ KEÇ
    // =========================================================
    const handleNavigate = (product) => {
        const targetId = product?.productId || product?._id;

        if (!targetId) {
            toast.error(
                "Məhsulun ətraflı məlumatına keçmək mümkün olmadı. Məhsul identifikatoru tapılmadı."
            );

            return;
        }

        if (product?.productType === "pagination") {
            navigate(`/pagination/${targetId}`);
        } else {
            navigate(`/product/${targetId}`);
        }
    };

    // =========================================================
    // MƏHSULLARI YENİDƏN YÜKLƏ
    // =========================================================
    const handleRetry = async () => {
        try {
            await refetch().unwrap?.();

            toast.success(
                "Seçilmiş məhsullar uğurla yeniləndi."
            );
        } catch (err) {
            console.error(
                "Seçilmiş məhsulların yenidən yüklənməsi zamanı xəta:",
                err
            );

            toast.error(
                `Məhsulları yenidən yükləmək mümkün olmadı. Səbəb: ${getErrorMessage(
                    err
                )}`
            );
        }
    };

    return (
        <div className="container min-h-[440px] mx-auto px-4 py-8 max-w-5xl">

            {/* =================================================
                BAŞLIQ
            ================================================= */}
            <div className="flex items-center gap-3 mb-6 border-b pb-4 dark:border-zinc-800">
                <Heart
                    size={28}
                    className="text-red-500 fill-red-500 flex-shrink-0"
                />

                <h1 className="text-2xl sm:text-3xl font-bold dark:text-white text-gray-800">
                    Seçilmişlər siyahısı
                </h1>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                Bəyəndiyiniz və yadda saxladığınız məhsulları burada görə
                bilərsiniz.
            </p>

            {/* =================================================
                YÜKLƏNMƏ VƏZİYYƏTİ
            ================================================= */}
            {isLoading ? (
                <div className="flex flex-col justify-center items-center py-20 bg-gray-50 dark:bg-zinc-900 rounded-2xl border dark:border-zinc-800">
                    <Loader2
                        size={48}
                        className="animate-spin text-blue-600 dark:text-blue-400 mb-5"
                    />

                    <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                        Seçilmiş məhsullar yüklənir...
                    </h2>

                    <p className="text-gray-500 dark:text-gray-400 text-sm mt-2 text-center">
                        Seçilmişlər siyahınız hazırlanır. Zəhmət olmasa,
                        gözləyin.
                    </p>
                </div>

            ) : isError ? (

                /* =================================================
                    MƏHSULLARIN YÜKLƏNMƏSİ XƏTASI
                ================================================= */
                <div className="text-center py-16 px-6 bg-red-50 dark:bg-red-950/20 rounded-2xl border border-red-200 dark:border-red-900">

                    <AlertTriangle
                        size={48}
                        className="mx-auto text-red-500 mb-5"
                    />

                    <h2 className="text-xl sm:text-2xl font-bold text-red-800 dark:text-red-300">
                        Seçilmiş məhsullar yüklənmədi
                    </h2>

                    <p className="text-gray-600 dark:text-gray-400 mt-3">
                        Seçilmişlər siyahısının alınması zamanı problem
                        yarandı.
                    </p>

                    <div className="mt-5 max-w-2xl mx-auto text-left bg-white dark:bg-zinc-900 border border-red-200 dark:border-red-900 rounded-xl p-4">
                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                            Xətanın səbəbi:
                        </p>

                        <p className="text-sm text-gray-600 dark:text-gray-400 break-words">
                            {getErrorMessage(error)}
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={handleRetry}
                        disabled={isFetching}
                        className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold transition"
                    >
                        {isFetching ? (
                            <>
                                <Loader2
                                    size={18}
                                    className="animate-spin"
                                />

                                Yenidən yüklənir...
                            </>
                        ) : (
                            <>
                                <RefreshCw size={18} />

                                Yenidən yoxla
                            </>
                        )}
                    </button>
                </div>

            ) : !data || data.length === 0 ? (

                /* =================================================
                    BOŞ SİYAHI
                ================================================= */
                <div className="text-center py-20 bg-gray-50 dark:bg-zinc-900 rounded-2xl border dark:border-zinc-800">

                    <PackageOpen
                        size={58}
                        className="mx-auto text-gray-400 dark:text-gray-500 mb-5"
                    />

                    <h2 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white">
                        Seçilmiş məhsul yoxdur
                    </h2>

                    <p className="text-gray-500 dark:text-gray-400 text-base mt-3">
                        Hazırda seçilmişlər siyahınızda heç bir məhsul yoxdur.
                    </p>

                    <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
                        Bəyəndiyiniz məhsulları seçilmişlər siyahısına əlavə
                        etdikdə onlar burada görünəcək.
                    </p>
                </div>

            ) : (

                /* =================================================
                    MƏHSUL SİYAHISI
                ================================================= */
                <div className="grid grid-cols-1 gap-4">

                    {data.map((product) => {

                        const imageUrl = Array.isArray(product?.photo)
                            ? product.photo[0]
                            : product?.photo ||
                              product?.thumbnail ||
                              "https://via.placeholder.com/150";

                        const isDeleting =
                            deletingId === product?._id;

                        return (
                            <div
                                key={product?._id}
                                className="group relative bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 shadow-sm hover:shadow-xl rounded-2xl p-4 sm:p-5 transition-all duration-300 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 overflow-hidden"
                            >

                                {/* =================================================
                                    MƏHSUL ŞƏKLİ
                                ================================================= */}
                                <div
                                    onClick={() =>
                                        !isDeleting &&
                                        handleNavigate(product)
                                    }
                                    className={`relative w-full sm:w-40 h-44 sm:h-36 rounded-xl overflow-hidden bg-gray-100 dark:bg-zinc-800 flex-shrink-0 group/img ${
                                        isDeleting
                                            ? "cursor-not-allowed opacity-70"
                                            : "cursor-pointer"
                                    }`}
                                >
                                    <img
                                        src={imageUrl}
                                        alt={
                                            product?.title ||
                                            "Məhsul şəkli"
                                        }
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover/img:scale-110"
                                        onError={(e) => {
                                            e.currentTarget.onerror = null;

                                            e.currentTarget.src =
                                                "https://via.placeholder.com/150?text=S%C9%99kil+Yoxdur";
                                        }}
                                    />

                                    {/* ŞƏKİL ÜZRƏ HOVER */}
                                    {!isDeleting && (
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2 text-white font-medium text-xs">
                                            <Eye size={18} />

                                            <span>
                                                Ətraflı bax
                                            </span>
                                        </div>
                                    )}

                                    {/* YÖNLƏNDİRMƏ NİŞANI */}
                                    {!isDeleting && (
                                        <span className="absolute top-2 right-2 bg-black/60 backdrop-blur-md text-white p-1.5 rounded-full opacity-80 group-hover/img:opacity-100 transition-all">
                                            <ArrowRight
                                                size={14}
                                                className="-rotate-45"
                                            />
                                        </span>
                                    )}

                                    {/* SİLİNİR VƏZİYYƏTİ */}
                                    {isDeleting && (
                                        <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center text-white">
                                            <Loader2
                                                size={28}
                                                className="animate-spin mb-2"
                                            />

                                            <span className="text-xs font-semibold">
                                                Silinir...
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* =================================================
                                    MƏHSUL MƏLUMATLARI
                                ================================================= */}
                                <div className="flex-1 w-full flex flex-col justify-between h-full">

                                    <div>

                                        <div className="flex justify-between items-start gap-2">

                                            <h3
                                                onClick={() =>
                                                    !isDeleting &&
                                                    handleNavigate(product)
                                                }
                                                className={`text-base sm:text-lg font-bold dark:text-white text-gray-800 line-clamp-2 ${
                                                    isDeleting
                                                        ? "cursor-not-allowed opacity-60"
                                                        : "cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                                                } transition-colors`}
                                            >
                                                {product?.title ||
                                                    "Məhsul adı yoxdur"}
                                            </h3>

                                        </div>

                                        {/* KATEQORİYA */}
                                        <div className="flex flex-wrap items-center gap-2 mt-2">

                                            {product?.catagory && (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-gray-300 text-xs rounded-lg font-medium">
                                                    <Tag size={12} />

                                                    {product.catagory}
                                                </span>
                                            )}

                                        </div>
                                    </div>

                                    {/* =================================================
                                        STOK VƏZİYYƏTİ
                                    ================================================= */}
                                    <div className="mt-3">

                                        {product?.stock === 1 ? (

                                            <span className="text-xs font-semibold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 px-2.5 py-1 rounded-md">
                                                Son 1 məhsul qalıb
                                            </span>

                                        ) : product?.stock === 0 ? (

                                            <span className="text-xs font-semibold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 px-2.5 py-1 rounded-md">
                                                Stokda yoxdur
                                            </span>

                                        ) : product?.stock > 1 ? (

                                            <span className="text-xs font-semibold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/40 px-2.5 py-1 rounded-md">
                                                Stokda {product.stock} ədəd
                                                var
                                            </span>

                                        ) : null}

                                    </div>
                                </div>

                                {/* =================================================
                                    QİYMƏT VƏ DÜYMƏLƏR
                                ================================================= */}
                                <div className="w-full sm:w-auto flex sm:flex-col justify-between sm:justify-center items-center sm:items-end gap-3 pt-3 sm:pt-0 border-t sm:border-t-0 dark:border-zinc-800">

                                    {/* QİYMƏT */}
                                    <div className="text-left sm:text-right">

                                        <span className="text-xs text-gray-400 block">
                                            Qiymət
                                        </span>

                                        <span className="text-xl font-extrabold text-blue-600 dark:text-blue-400">
                                            {product?.price ??
                                                "Məlumat yoxdur"}{" "}
                                            ₼
                                        </span>

                                    </div>

                                    <div className="flex items-center gap-2">

                                        {/* ƏTRAFLI BAX */}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleNavigate(product)
                                            }
                                            disabled={isDeleting}
                                            className="px-3 py-2 bg-gray-100 hover:bg-blue-50 dark:bg-zinc-800 dark:hover:bg-blue-900/30 text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <span>
                                                Ətraflı bax
                                            </span>

                                            <ExternalLink size={14} />
                                        </button>

                                        {/* SİL */}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleDelete(
                                                    product?._id
                                                )
                                            }
                                            disabled={
                                                isDeleting ||
                                                deletingId !== null
                                            }
                                            className="p-2 bg-red-50 hover:bg-red-100 dark:bg-red-950/30 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            title="Seçilmişlərdən sil"
                                            aria-label="Seçilmişlərdən sil"
                                        >
                                            {isDeleting ? (
                                                <Loader2
                                                    size={16}
                                                    className="animate-spin"
                                                />
                                            ) : (
                                                <Trash2 size={16} />
                                            )}
                                        </button>

                                    </div>
                                </div>
                            </div>
                        );
                    })}

                </div>
            )}

            {/* =================================================
                SİLİNƏN ZAMANI ÜMUMİ MƏLUMAT
            ================================================= */}
            {deletingId && (
                <div className="mt-6 flex items-center justify-center gap-3 text-sm text-gray-500 dark:text-gray-400">

                    <Loader2
                        size={17}
                        className="animate-spin text-red-500"
                    />

                    <span>
                        Seçilmiş məhsul siyahıdan silinir. Zəhmət olmasa,
                        gözləyin...
                    </span>

                </div>
            )}
        </div>
    );
};

export default Favorie;